/**
 * 💬 웍스 「포털챗봇」 연결 화면 — /helpbot-link#t=열쇠 (v2.282.0 · 2026-10-19 · 사용자 확정 C안)
 *
 * 메일 연결이 없는 직원이 웍스에서 포털챗봇에게 처음 말을 걸면, 봇이 그 사람 1:1 에만 이 주소를 보낸다.
 * 여기서 로그인한 본인이 [연결]을 누르면 그 웍스 계정 ↔ 이 포털 계정이 짝이 된다(처음 한 번).
 *   · 열쇠는 주소의 # 뒤 — 브라우저가 서버로 보내지 않아 접속 기록에 남지 않는다. 로그인을 거쳐도 # 은 따라온다(Login.tsx).
 *   · 열쇠는 30분 · 한 번. 연결 전에 웍스로 보낸 질문은 연결하자마자 도우미에게 넘어가고, 답은 웍스로 간다.
 */

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../lib/auth-context';
import * as hb from '../lib/helpbot-api';

const ROLE_LABEL: Record<string, string> = { admin: '관리자', mgmt: '경영지원', sales: '영업', viewer: '조회 전용' };

export function HelpbotLinkPage() {
  const { user } = useAuth();
  const { hash } = useLocation();
  // 처음 열 때의 열쇠를 붙잡아 둔다 — 연결 뒤 주소에서 지워도 화면은 결과를 보여 준다
  const [token, setToken] = useState(() => hb.linkTokenFromHash(hash));
  const linkM = useMutation({
    mutationFn: (t: string) => hb.confirmWorksLink(t),
    onSuccess: () => {
      // 다 쓴 열쇠를 주소에서 걷는다(복사·공유돼도 쓸 수 없지만 남길 까닭이 없다)
      window.history.replaceState(null, '', window.location.pathname);
    },
  });
  // 같은 탭에서 새 링크를 열면 # 뒤만 바뀌어 화면이 새로 뜨지 않는다 — 새 열쇠면 처음 화면으로
  const { reset } = linkM;
  useEffect(() => {
    const t = hb.linkTokenFromHash(hash);
    if (t && t !== token) { setToken(t); reset(); }
  }, [hash, token, reset]);
  const r = linkM.data;

  return (
    <div className="mx-auto max-w-lg px-4 py-8">
      <div className="rounded border border-slate-200 bg-white p-5 shadow-sm">
        <h1 className="ui-page-title">웍스 「포털챗봇」 연결</h1>

        {!token && (
          <p className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            링크가 올바르지 않아요. 웍스에서 포털챗봇에게 다시 말을 걸면 새 연결 링크가 와요.
          </p>
        )}

        {token && !r && (
          <>
            <p className="mt-3 text-sm leading-6 text-slate-700">
              웍스 메신저의 포털챗봇 대화를 <b>이 포털 계정</b>에 연결합니다. 연결하면 웍스에서 물어본 것에
              이 계정이 화면에서 볼 수 있는 자료만으로 답해요.
            </p>
            <dl className="mt-3 grid grid-cols-[80px_1fr] gap-y-1 rounded bg-slate-50 px-3 py-2 text-sm">
              <dt className="text-slate-500">포털 계정</dt>
              <dd className="text-slate-800">{user ? `${user.name} (${user.userId})` : '…'}</dd>
              <dt className="text-slate-500">권한</dt>
              <dd className="text-slate-800">{user ? (ROLE_LABEL[user.role] ?? user.role) : '…'}</dd>
            </dl>
            <p className="mt-2 text-xs text-slate-500">
              본인 웍스에서 받은 링크가 아니면 연결하지 마세요. 처음 한 번만 하면 되고, 다른 웍스 계정으로 다시 연결하면 앞 연결은 풀려요.
            </p>
            {linkM.isError && (
              <p className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {linkM.error instanceof Error ? linkM.error.message : '잠시 후 다시 시도해 주세요'}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Link to="/" className="btn btn-secondary">취소</Link>
              <button type="button" className="btn btn-primary" disabled={linkM.isPending || !user} onClick={() => linkM.mutate(token)}>
                {linkM.isPending ? '연결하는 중…' : '연결'}
              </button>
            </div>
          </>
        )}

        {r && (
          <div className="mt-3 space-y-2 text-sm leading-6 text-slate-700">
            <p className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900">
              ✅ 연결됐어요 — 이제 웍스 포털챗봇에게 바로 물어보면 돼요.
            </p>
            {r.queued && <p>방금 웍스로 보낸 질문은 지금 도우미가 찾아보는 중이에요. 답은 웍스로 가요.</p>}
            {r.notQueuedReason === 'open-exists' && <p>앞 질문의 답을 기다리는 중이라 방금 질문은 넣지 못했어요 — 답이 온 뒤 웍스에서 다시 보내 주세요.</p>}
            {r.notQueuedReason === 'daily-limit' && <p>오늘 물어볼 수 있는 횟수를 다 써서 방금 질문은 넣지 못했어요 — 내일 다시 보내 주세요.</p>}
            {!r.notified && <p className="text-xs text-slate-500">웍스로 「연결됐어요」 안내를 보내지 못했지만 연결은 됐어요.</p>}
            <p className="text-xs text-slate-500">질문과 답은 포털 오른쪽 위 [💬 사용 문의] 창의 대화 목록에서도 볼 수 있어요.</p>
            <div className="flex justify-end">
              <Link to="/" className="btn btn-secondary">포털 첫 화면</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
